import { useCallback, useEffect, useState } from "react";

const ROOT_CLASS = "planner-fullscreen";

type WebkitDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
};

type WebkitElement = HTMLElement & {
  webkitRequestFullscreen?: () => void;
};

function fullscreenElement(): Element | null {
  const doc = document as WebkitDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

async function requestDocumentFullscreen(): Promise<void> {
  const el = document.documentElement as WebkitElement;
  if (typeof el.requestFullscreen === "function") {
    await el.requestFullscreen();
    return;
  }
  el.webkitRequestFullscreen?.();
}

async function exitDocumentFullscreen(): Promise<void> {
  const doc = document as WebkitDocument;
  if (!fullscreenElement()) return;
  if (typeof document.exitFullscreen === "function") {
    await document.exitFullscreen();
    return;
  }
  doc.webkitExitFullscreen?.();
}

export function usePlannerFullscreen() {
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle(ROOT_CLASS, fullscreen);
    return () => {
      document.documentElement.classList.remove(ROOT_CLASS);
    };
  }, [fullscreen]);

  useEffect(() => {
    const onChange = () => {
      if (!fullscreenElement()) setFullscreen(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange as EventListener);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange as EventListener);
      if (fullscreenElement() === document.documentElement) {
        void exitDocumentFullscreen().catch(() => {});
      }
    };
  }, []);

  const exit = useCallback(() => {
    if (fullscreenElement()) {
      void exitDocumentFullscreen().catch(() => setFullscreen(false));
      return;
    }
    setFullscreen(false);
  }, []);

  const enter = useCallback(() => {
    setFullscreen(true);
    if (!fullscreenElement()) {
      void requestDocumentFullscreen().catch(() => {
        // Browser lehnt die Fullscreen-API ab; das Layout bleibt trotzdem vollflächig.
      });
    }
  }, []);

  const toggle = useCallback(() => {
    if (fullscreen) exit();
    else enter();
  }, [enter, exit, fullscreen]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (document.querySelector("[role='dialog']")) return;
      exit();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [exit, fullscreen]);

  return { fullscreen, toggle };
}
